import { Link } from "react-router-dom";
import { Calendar, MapPin, Trophy, ArrowLeft, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const scorers = {
  CAN: [
    { time: "04:12 P1", player: "Connor Bedard", assists: "S. Crosby, C. McDavid", strength: "EV" },
    { time: "11:47 P1", player: "Nathan MacKinnon", assists: "C. Makar", strength: "PP" },
    { time: "08:23 P2", player: "Sidney Crosby", assists: "C. McDavid, B. Marchand", strength: "EV" },
    { time: "15:02 P2", player: "Brayden Point", assists: "M. Marner", strength: "EV" },
    { time: "18:41 P3", player: "Connor McDavid", assists: "(empty net)", strength: "EN" },
  ],
  SWE: [
    { time: "07:55 P1", player: "William Nylander", assists: "E. Lindholm, V. Hedman", strength: "EV" },
    { time: "13:30 P2", player: "Elias Pettersson", assists: "L. Raymond", strength: "PP" },
    { time: "06:18 P3", player: "Rasmus Dahlin", assists: "M. Forsberg, A. Karlsson", strength: "EV" },
  ],
};

const groupBStandings = [
  { team: "🇨🇦 Canada", gp: 1, w: 1, otw: 0, otl: 0, l: 0, gf: 5, ga: 3, pts: 3, highlight: true },
  { team: "🇨🇿 Czechia", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇩🇰 Denmark", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇸🇰 Slovakia", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇳🇴 Norway", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇸🇮 Slovenia", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇮🇹 Italy", gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 },
  { team: "🇸🇪 Sweden", gp: 1, w: 0, otw: 0, otl: 0, l: 1, gf: 3, ga: 5, pts: 0, highlight: true },
];

const MatchCanSwe = () => {
  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-6">
      <div className="container mx-auto max-w-4xl">
        <Link
          to="/#big-events"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tournaments
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
            IIHF World Championship 2026 · Group B · Game Day 1
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Canada vs Sweden
          </h1>
          <div className="flex items-center justify-center gap-6 md:gap-12 my-8">
            <div className="text-center">
              <div className="text-6xl mb-2">🇨🇦</div>
              <div className="text-2xl font-semibold">CAN</div>
              <div className="text-5xl font-display font-bold text-primary mt-2">5</div>
            </div>
            <div className="text-2xl text-muted-foreground font-light">FINAL</div>
            <div className="text-center">
              <div className="text-6xl mb-2">🇸🇪</div>
              <div className="text-2xl font-semibold">SWE</div>
              <div className="text-5xl font-display font-bold text-muted-foreground mt-2">3</div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary" />
              <div>
                <div className="text-xs text-muted-foreground">Date</div>
                <div className="font-medium">May 15, 2026</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <div>
                <div className="text-xs text-muted-foreground">Puck Drop</div>
                <div className="font-medium">16:20 CET</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="w-5 h-5 text-primary" />
              <div>
                <div className="text-xs text-muted-foreground">Venue</div>
                <div className="font-medium">BCF Arena, Fribourg</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scorers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span>🇨🇦</span> Canada Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {scorers.CAN.map((g, i) => (
                <div key={i} className="border-l-2 border-primary pl-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{g.player}</div>
                    <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
                      {g.strength}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{g.time}</div>
                  <div className="text-xs text-muted-foreground">A: {g.assists}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span>🇸🇪</span> Sweden Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {scorers.SWE.map((g, i) => (
                <div key={i} className="border-l-2 border-muted-foreground/40 pl-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{g.player}</div>
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                      {g.strength}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{g.time}</div>
                  <div className="text-xs text-muted-foreground">A: {g.assists}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Standings Impact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="w-5 h-5 text-primary" />
              Group B Standings Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Canada climbs to the top of Group B with 3 points after a regulation win.
              Sweden remains winless and will look to bounce back vs Denmark on May 17.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-left py-2 px-2">Team</th>
                    <th className="px-2">GP</th>
                    <th className="px-2">W</th>
                    <th className="px-2">L</th>
                    <th className="px-2">GF</th>
                    <th className="px-2">GA</th>
                    <th className="px-2">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {groupBStandings.map((s) => (
                    <tr
                      key={s.team}
                      className={`border-b border-border/50 ${
                        s.highlight ? "bg-primary/5 font-medium" : ""
                      }`}
                    >
                      <td className="py-2 px-2">{s.team}</td>
                      <td className="text-center px-2">{s.gp}</td>
                      <td className="text-center px-2">{s.w}</td>
                      <td className="text-center px-2">{s.l}</td>
                      <td className="text-center px-2">{s.gf}</td>
                      <td className="text-center px-2">{s.ga}</td>
                      <td className="text-center px-2 font-semibold">{s.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MatchCanSwe;
